import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  useOpenInspectionsQuery,
  usePdfProfilesQuery,
  type OpenInspectionRow,
} from "@/hooks/useOpenInspectionsQuery";
import { useAssignableProfilesForInspections } from "@/hooks/useAssignableProfiles";
import { isDueWithinWindow } from "@/lib/openInspectionsWindow";
import {
  loadFilterState,
  saveFilterState,
  DEFAULT_OPEN_INSPECTIONS_FILTERS,
  type OpenInspectionsFilterState,
} from "@/lib/openInspectionsFilterStore";
import { OpenInspectionsCards, type GroupCount } from "./OpenInspectionsCards";
import { OpenInspectionsFilterBar, type FilterOption } from "./OpenInspectionsFilterBar";
import { OpenInspectionsTable } from "./OpenInspectionsTable";
import { AssignInspectionDialog } from "./AssignInspectionDialog";
import { PdfProfilesSection } from "./PdfProfilesSection";

/** Extra (card-driven) drilldown that is not part of the persisted filter state. */
type Drilldown = "none" | "dueSoon" | "unassigned";

/** Status sort weight: actionable-first (overdue → in_progress → pending). */
const STATUS_SORT_WEIGHT: Record<string, number> = {
  overdue: 0,
  in_progress: 1,
  pending: 2,
  completed: 3,
  cancelled: 4,
};

/**
 * "Offene Prüfungen" dashboard view (TES-506).
 * Overview cards (next-14-days / unassigned / by-team) + filterable, sortable
 * table of open inspections + PDF-Vorlagen subsection. Filter state persists
 * client-side (localStorage, GAP 4). All data via RLS-scoped client.
 */
export function OpenInspectionsPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { inspections, isLoading, isFetching, refetch } = useOpenInspectionsQuery();
  const { data: pdfProfiles = [] } = usePdfProfilesQuery();
  const { data: assignableUsers = [] } = useAssignableProfilesForInspections();

  const [filters, setFilters] = useState<OpenInspectionsFilterState>(
    DEFAULT_OPEN_INSPECTIONS_FILTERS,
  );
  const [drilldown, setDrilldown] = useState<Drilldown>("none");
  const [assignTarget, setAssignTarget] = useState<OpenInspectionRow | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  // Hydrate persisted filters once on mount (localStorage may be blocked → defaults).
  useEffect(() => {
    setFilters(loadFilterState());
  }, []);

  const updateFilters = useCallback((next: Partial<OpenInspectionsFilterState>) => {
    setFilters((prev) => {
      const merged = { ...prev, ...next };
      saveFilterState(merged);
      return merged;
    });
    // Changing an explicit filter clears the card-driven drilldown overlay.
    setDrilldown("none");
  }, []);

  // ── Derived: card metrics over the full open set ──
  const dueSoonCount = useMemo(
    () => inspections.filter((i) => isDueWithinWindow(i.due_date)).length,
    [inspections],
  );
  const unassignedCount = useMemo(
    () => inspections.filter((i) => i.assigned_to === null).length,
    [inspections],
  );
  // "Offen nach Gruppe" (TES-2017 v2.3): aggregate over assigned_group_id (Fachbereich-Gruppe).
  const byGroup = useMemo<GroupCount[]>(() => {
    const map = new Map<string, GroupCount>();
    inspections.forEach((i) => {
      if (!i.group_id) return;
      const existing = map.get(i.group_id);
      if (existing) existing.count += 1;
      else
        map.set(i.group_id, {
          groupId: i.group_id,
          groupName: i.group_name ?? i.group_id,
          count: 1,
        });
    });
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [inspections]);

  // ── Derived: filter option lists (from the visible rows) ──
  const groupOptions = useMemo<FilterOption[]>(
    () => byGroup.map((g) => ({ id: g.groupId, name: g.groupName })),
    [byGroup],
  );
  const checklistOptions = useMemo<FilterOption[]>(() => {
    const map = new Map<string, string>();
    inspections.forEach((i) => {
      if (i.checklist_id) map.set(i.checklist_id, i.checklist_name ?? i.checklist_id);
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [inspections]);

  // ── Derived: filtered + sorted table rows ──
  const visibleRows = useMemo(() => {
    let rows = inspections;

    if (drilldown === "dueSoon") rows = rows.filter((i) => isDueWithinWindow(i.due_date));
    if (drilldown === "unassigned") rows = rows.filter((i) => i.assigned_to === null);

    if (filters.groupId !== "all") rows = rows.filter((i) => i.group_id === filters.groupId);
    if (filters.status !== "all") rows = rows.filter((i) => i.status === filters.status);
    if (filters.checklistId !== "all")
      rows = rows.filter((i) => i.checklist_id === filters.checklistId);

    const sorted = [...rows];
    sorted.sort((a, b) => {
      if (filters.sortBy === "status") {
        return (STATUS_SORT_WEIGHT[a.status] ?? 9) - (STATUS_SORT_WEIGHT[b.status] ?? 9);
      }
      if (filters.sortBy === "created_at") {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      // due_date: nulls last, ascending.
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });
    return sorted;
  }, [inspections, drilldown, filters]);

  const handleAssign = (inspection: OpenInspectionRow) => {
    setAssignTarget(inspection);
    setAssignOpen(true);
  };
  const handleEdit = (inspectionId: string) => navigate(`/inspections/${inspectionId}`);

  const drilldownLabel =
    drilldown === "dueSoon"
      ? t("openInspections.drilldown.dueSoon")
      : drilldown === "unassigned"
        ? t("openInspections.drilldown.unassigned")
        : null;

  return (
    <div className="space-y-6" data-testid="open-inspections-panel">
      <OpenInspectionsCards
        dueSoonCount={dueSoonCount}
        unassignedCount={unassignedCount}
        byGroup={byGroup}
        onDrilldownDueSoon={() => setDrilldown("dueSoon")}
        onDrilldownUnassigned={() => setDrilldown("unassigned")}
        onDrilldownGroup={(groupId) => updateFilters({ groupId })}
      />

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg">{t("openInspections.tableTitle")}</CardTitle>
            <CardDescription>{t("openInspections.tableSubtitle")}</CardDescription>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label={t("common.refresh")}
            data-testid="open-inspections-refresh"
          >
            <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <OpenInspectionsFilterBar
            filters={filters}
            groups={groupOptions}
            checklists={checklistOptions}
            onChange={updateFilters}
          />

          {drilldownLabel && (
            <div
              className="flex items-center gap-2 text-sm"
              data-testid="open-inspections-drilldown-chip"
            >
              <span className="text-muted-foreground">{t("common.filter")}:</span>
              <button
                type="button"
                onClick={() => setDrilldown("none")}
                className="rounded-full bg-secondary px-2.5 py-0.5 hover:bg-secondary/80"
              >
                {drilldownLabel} ✕
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="space-y-2" data-testid="open-inspections-loading">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <OpenInspectionsTable
              inspections={visibleRows}
              onAssign={handleAssign}
              onEdit={handleEdit}
            />
          )}
        </CardContent>
      </Card>

      <PdfProfilesSection profiles={pdfProfiles} />

      <AssignInspectionDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        inspectionId={assignTarget?.id ?? null}
        inspectionLabel={
          assignTarget?.position_name ??
          assignTarget?.checklist_name ??
          t("openInspections.noPosition")
        }
        currentAssignee={assignTarget?.assigned_to ?? null}
        assignableUsers={assignableUsers}
      />
    </div>
  );
}
